import { Box, Button, Heading, Input, InputImperativeActions, Text } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { SearchSmall } from '@wix/wix-ui-icons-common'
import classNames from 'classnames'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { isDomainSupportVoucher } from '../../../api/isDomainSupportVoucher'
import { usePageLogger } from '../../../bi/mfi'
import { DesignSystemTokensLoader } from '../../../components/DesignSystemTokensLoader'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import Done from '../../reusable/icons/Done.svg'
import WixLogo from '../../reusable/icons/WixLogo.svg'
import { WixMobileBackButton } from '../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { TWixMobileDomainNF as Content } from './WixMobileDomainNF.content'
import { wixMobileDomainNFDataHooks as dataHooks } from './WixMobileDomainNF.dataHooks'
import s from './WixMobileDomainNF.scss'

export const WixMobileDomainNF = () => {
  const { content } = useContent<Content>()

  if (content.skin === 'standard') {
    return <WixMobileDomainNFTemplate />
  }

  return (
    <DesignSystemTokensLoader type="odeditor">
      <WixMobileDomainNFTemplate />
    </DesignSystemTokensLoader>
  )
}

export const WixMobileDomainNFTemplate: FC = () => {
  const { content } = useContent<Content>()
  const [searchParams] = useSearchParams()
  const { suggestedDomain = searchParams.get('domainName') || '' } = useStorageContext()
  const [domain, setDomain] = useState(suggestedDomain)
  const [isDomainVoucherSupported, setIsDomainVoucherSupported] = useState(false)
  const [pageLoading] = usePageLoading()
  const pageLogger = usePageLogger()
  const next = useNext()

  const isShowDomainBanner = content.domainBanner && isDomainVoucherSupported

  const getDomainVoucherSupported = async () => {
    if (!suggestedDomain) {
      return
    }
    try {
      const domainsResponse = await isDomainSupportVoucher(domain)
      setIsDomainVoucherSupported(domainsResponse.data.isVoucherSupported)
      void pageLogger.generalAction({ action: JSON.stringify(domainsResponse.data) })
    } catch (err) {
      console.error('GET_VOUCHER_ERR', err)
    }
  }

  const onNextButtonClick = async () => {
    if (pageLoading) {
      return
    }

    await pageLogger.answer({ answer: domain })
    next({ [content.question.questionKey]: { values: [domain] } })
  }

  const onSkipButtonClick = () => {
    if (pageLoading) {
      return
    }
    void pageLogger.skip()
    next({ [content.question.questionKey]: { skipped: true } })
  }

  const onBackButtonClick = () => {
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }

  const inputRef = useRef<InputImperativeActions>(null)

  useEffect(() => {
    void pageLogger.screenView({ initialData: suggestedDomain })
    inputRef.current?.input?.setAttribute('enterkeyhint', 'search')

    if (content.domainBannerControl === 'isVoucherSupported') {
      getDomainVoucherSupported()
    } else if (content.domainBannerControl === 'alwaysShow') {
      setIsDomainVoucherSupported(true)
    }
  }, [pageLogger])

  return (
    <WixMobilePage skin={content.skin === 'standard' ? 'standard' : 'new-funnel'}>
      <WixMobileHeader
        leftContent={
          content.skin === 'standard' ? (
            <WixMobileBackButton onBack={onBackButtonClick} dataHook={dataHooks.back()} />
          ) : (
            <WixLogo height="24" />
          )
        }
        hideLogo={content.skin !== 'standard'}
        skin={content.skin === 'standard' ? 'standard' : 'new-funnel'}
        rightContent={
          (content.skip && (
            <WixMobileSkipButton dataHook={dataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
          )) ||
          undefined
        }
      />
      {content.successMessage && (
        <div className={s.successMessageContainer}>
          <Done />
          <Text dataHook={dataHooks.successMessage()}>{content.successMessage}</Text>
        </div>
      )}
      <WixMobileContentWrapper className={s.container}>
        <div
          className={classNames(s.card, {
            [s.standardCard]: content.skin === 'standard',
            [s.newFunnelCard]: content.skin !== 'standard',
          })}
        >
          <Box textAlign="center">
            <Heading size="extraLarge" dataHook={dataHooks.title()}>
              {content.question.content.title}
            </Heading>
          </Box>
          <Input
            ref={inputRef}
            value={domain}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onNextButtonClick()
              }
            }}
            prefix={
              <Input.Affix>
                <SearchSmall color={content.skin === 'standard' ? '#116DFF' : '#000624'} />
              </Input.Affix>
            }
            onChange={e => {
              if (content.domainBannerControl === 'isVoucherSupported') {
                setIsDomainVoucherSupported(false)
              }
              setDomain(e.target.value)
            }}
            placeholder={content.question.content.placeholder}
            dataHook={dataHooks.input()}
            clearButton
            border={content.skin === 'standard' ? 'round' : 'standard'}
            onClear={() => setDomain('')}
            className={s.input}
            size="large"
          />

          <Box marginTop="24px" textAlign="center" padding="0 12px">
            <Text dataHook={dataHooks.subtitle()}>{content.question.content.subtitle}</Text>
          </Box>

          {isShowDomainBanner && (
            <Box background="#DFF994" padding="12px" borderRadius="6px" marginTop="20px" gap="8px" verticalAlign="top">
              <PresentIcon />
              <Text dataHook={dataHooks.domainBanner()} skin="success" weight="bold">
                <span style={{ color: '#016F3A' }}>{content.domainBanner}</span>
              </Text>
            </Box>
          )}
        </div>
      </WixMobileContentWrapper>
      <WixMobileFooter>
        {content.next && (
          <Button
            size="large"
            skin={content.skin === 'standard' ? 'standard' : 'dark'}
            fullWidth
            className={s.nextButton}
            onClick={onNextButtonClick}
            dataHook={dataHooks.next()}
            disabled={domain === ''}
          >
            {content.next}
          </Button>
        )}
      </WixMobileFooter>
    </WixMobilePage>
  )
}

const PresentIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    style={{ flexShrink: 0, paddingTop: '2px' }}
  >
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M7.5 5C6.67157 5 6 4.32843 6 3.5C6 2.67157 6.67157 2 7.5 2C7.84388 2 8.51809 2.03687 9.13452 2.96327C9.23818 3.11905 9.36 3.36063 9.5 3.68802C9.64 3.36063 9.76182 3.11905 9.86548 2.96327C10.4819 2.03687 11.1561 2 11.5 2C12.3284 2 13 2.67157 13 3.5C13 4.32843 12.3284 5 11.5 5H15C15.5523 5 16 5.44772 16 6V9C16 9 15.5523 9 15 9V14C15 14.5523 14.5523 15 14 15H5C4.44772 15 4 14.5523 4 14V9C3.44772 9 3 9 3 9V6C3 5.44772 3.44772 5 4 5H7.5ZM4 6V8H9V6H4ZM10 6V8H15V6H10ZM5 9V14H9V9H5ZM10 9V14H14V9H10ZM11.5 4.25C11.9142 4.25 12.25 3.91421 12.25 3.5C12.25 3.08579 11.9142 2.75 11.5 2.75C11.0935 2.75 10.8332 2.86275 10.4899 3.37875C10.3891 3.5302 10.2499 3.82405 10.0793 4.25H11.5ZM7.5 4.25H8.92074C8.75014 3.82405 8.6109 3.5302 8.51012 3.37875C8.16677 2.86275 7.9065 2.75 7.5 2.75C7.08579 2.75 6.75 3.08579 6.75 3.5C6.75 3.91421 7.08579 4.25 7.5 4.25Z"
      fill="#016F3A"
    />
  </svg>
)
